import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { useModalDismiss } from "../hooks/useModalDismiss";
import { RichSummary } from "./RichSummary";
import { STATE_LABEL, TYPE_META, stateLabel, windowLabel } from "./statusAlerts";
import type { AlertType, AnnouncementSeverity } from "../services/api";

/**
 * Full notice for one status alert. Right-hand drawer on desktop, full-screen
 * sheet at 560px. Focus starts on the title and returns to the opener because
 * useModalDismiss restores the previously focused element on close.
 */
export function AnnouncementDrawer({
  id,
  title,
  body,
  bodyHtml,
  severity,
  alertType,
  windowStart,
  windowEnd,
  version,
  lifecycleState,
  onClose,
}: {
  id: string;
  title: string;
  body: string;
  bodyHtml: string | null;
  severity: AnnouncementSeverity;
  alertType: AlertType | null;
  windowStart: string | null;
  windowEnd: string | null;
  version: string;
  lifecycleState: string | null;
  onClose: () => void;
}) {
  const panelRef = useModalDismiss<HTMLDivElement>(onClose, true, { trapHeading: true });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const win = windowLabel(alertType, windowStart, windowEnd);
  const updated = formatLastUpdated(version);
  const kind = noticeKind(alertType, severity);
  const chip = lifecycleState && lifecycleState in STATE_LABEL ? lifecycleState : null;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return createPortal(
    <div
      className="announcement-drawer-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        id={id}
        className="announcement-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="announcement-drawer-head">
          <div className="announcement-drawer-heading">
            <div className="announcement-drawer-kicker">
              <span className="announcement-kind">{kind}</span>
              {chip && (
                <span className={`sa-chip ${chip}`}>
                  <span className="d" aria-hidden="true" />
                  {stateLabel(chip)}
                </span>
              )}
            </div>
            <h2 id={titleId} tabIndex={-1} ref={headingRef}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="announcement-drawer-close"
            aria-label="Close"
            onClick={onClose}
          >
            <DrawerIcon />
          </button>
        </header>
        <div className="announcement-drawer-body">
          {win && (
            <div className="announcement-window">
              <span className="l">{win.label}</span>
              <span className="v">{win.value}</span>
            </div>
          )}
          {bodyHtml !== null ? (
            <RichSummary
              className="announcement-drawer-summary"
              html={bodyHtml}
              variant="portal"
            />
          ) : (
            body && (
              <div className="announcement-drawer-summary is-plain">{body}</div>
            )
          )}
          {updated && <p className="announcement-drawer-updated">{updated}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function noticeKind(
  alertType: AlertType | null,
  severity: AnnouncementSeverity,
): string {
  if (alertType && alertType in TYPE_META) return TYPE_META[alertType].label;
  if (severity === "incident") return "Incident";
  if (severity === "maintenance") return "Maintenance";
  return "Info";
}

function formatLastUpdated(iso: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const formatted = date.toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
  return `Last updated ${formatted} AWST`;
}

function DrawerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
